import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

export interface ConfirmDialogData {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  icon?: string;
  warn?: boolean;
  /**
   * When set, the confirm button stays disabled until the user types this word
   * (case-insensitive). A little resistance for actions that are easy to regret.
   */
  typeToConfirm?: string;
}

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule
  ],
  template: `
    <h2 mat-dialog-title class="dialog-title">
      @if (data.icon) {
        <mat-icon [class.warn-icon]="data.warn">{{ data.icon }}</mat-icon>
      }
      {{ data.title }}
    </h2>
    <mat-dialog-content>
      <p class="dialog-message">{{ data.message }}</p>
      @if (data.typeToConfirm) {
        <mat-form-field appearance="outline" class="type-to-confirm">
          <mat-label>Type "{{ data.typeToConfirm }}" to continue</mat-label>
          <input matInput [(ngModel)]="typed" autocomplete="off" cdkFocusInitial
                 (keydown.enter)="canConfirm() && onConfirm()" />
        </mat-form-field>
      }
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      @if (data.cancelLabel !== '') {
        <button mat-stroked-button (click)="onCancel()">
          {{ data.cancelLabel || 'Cancel' }}
        </button>
      }
      <button mat-raised-button
              [color]="data.warn ? 'warn' : 'primary'"
              [disabled]="!canConfirm()"
              (click)="onConfirm()">
        {{ data.confirmLabel || 'Confirm' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .dialog-title {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .warn-icon {
      color: #f57c00;
    }
    .dialog-message {
      line-height: 1.6;
      color: #444;
      font-size: 14px;
    }
    .type-to-confirm {
      width: 100%;
    }
    mat-dialog-actions {
      padding: 12px 24px;
    }
    mat-dialog-actions button {
      margin-left: 8px;
    }
  `]
})
export class ConfirmDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<ConfirmDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmDialogData
  ) {}

  typed = '';

  canConfirm(): boolean {
    if (!this.data.typeToConfirm) return true;
    return this.typed.trim().toLowerCase() === this.data.typeToConfirm.trim().toLowerCase();
  }

  onConfirm(): void {
    this.dialogRef.close(true);
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
